import React, { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, Filter, ChevronDown, MoreVertical,
  BellRing, Edit, Trash2, Eye, X, Plus,
  Calendar, Clock, Users, Globe, Settings,
  MessageCircle, AlertCircle, CheckCircle,
  Bell, Radio, Target, Send, UserCheck,
  ArrowUpRight, FileText, PinIcon, MapPin
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import { Alert, AlertDescription } from "../../components/ui/alert";
import AdminHeader from "../../Headers/AdminHeader";
import SharedFooter3 from "../../Footer/SharedFooter3";
import { useAnnouncement } from "../../modules/admin/hooks/useAnnouncement";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import AnnouncementModal from "./AnnouncementModal";
import { AnnouncementTypeEnum, AnnouncementPriorityEnum, AnnouncementVisibilityEnum, Announcement } from "../../modules/shared/model/AnnouncementInterfaces";


const Announcements = () => {
  const dispatch = useAppDispatch();

  // Add styles for HTML content rendering
  React.useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      .announcement-html-content h1,
      .announcement-html-content h2,
      .announcement-html-content h3,
      .announcement-html-content h4,
      .announcement-html-content h5,
      .announcement-html-content h6 {
        font-weight: 700;
        color: #111827;
        margin: 0.5em 0;
      }
      .announcement-html-content h1 { font-size: 1.5em; }
      .announcement-html-content h2 { font-size: 1.3em; }
      .announcement-html-content h3 { font-size: 1.15em; }
      .announcement-html-content h4 { font-size: 1em; }
      .announcement-html-content p {
        margin: 0.5em 0;
        line-height: 1.6;
      }
      .announcement-html-content ul,
      .announcement-html-content ol {
        margin: 0.5em 0;
        padding-left: 1.5em;
      }
      .announcement-html-content li {
        margin: 0.25em 0;
      }
      .announcement-html-content strong {
        font-weight: 600;
        color: #374151;
      }
      .announcement-html-content em {
        font-style: italic;
      }
      .announcement-html-content a {
        color: #3b82f6;
        text-decoration: underline;
      }
      .announcement-html-content a:hover {
        color: #2563eb;
      }
      .announcement-html-content code {
        background: #f3f4f6;
        padding: 0.2em 0.4em;
        border-radius: 3px;
        font-family: monospace;
        font-size: 0.9em;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Hooks
  const {
    announcements,
    loading,
    error,
    totalCount,
    hasNextPage,
    currentPage,
    currentLimit,
    fetchAnnouncements,
    createNewAnnouncement,
    updateExistingAnnouncement,
    deleteExistingAnnouncement,
    clearSelected,
    clearErrorState,
    setPage,
    setLimit
  } = useAnnouncement();


  // Local state
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedType, setSelectedType] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedPriority, setSelectedPriority] = useState("all");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState<Announcement | null>(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    type: { id: AnnouncementTypeEnum.General },
    priority: { id: AnnouncementPriorityEnum.Medium },
    visibility: { id: AnnouncementVisibilityEnum.All_Users },
    region: { id: "" },
    country: { id: "", name: "", code: "" },
    publishDate: "",
    expiryDate: "",
    isMobile: false,
    isEmail: false,
    isWeb: true,
    isText: true
  });

  // Calculate stats from announcements data
  const stats = useMemo(() => {
    if (!announcements || !Array.isArray(announcements)) return { active: 0, scheduled: 0, totalViews: 0, responseRate: 0 };

    // Filter out any undefined/null announcements
    const validAnnouncements = announcements.filter(announcement => announcement && announcement.publishDate);

    const now = new Date();
    const active = validAnnouncements.filter(announcement => {
      const publishDate = new Date(announcement.publishDate);
      const expiryDate = announcement.expiryDate ? new Date(announcement.expiryDate) : null;
      return publishDate <= now && (!expiryDate || expiryDate > now);
    }).length;

    const scheduled = validAnnouncements.filter(announcement => {
      const publishDate = new Date(announcement.publishDate);
      return publishDate > now;
    }).length;

    const totalViews = validAnnouncements.reduce((sum, announcement) =>
      sum + (announcement.stats?.views || 0), 0
    );

    const totalAcknowledged = validAnnouncements.reduce((sum, announcement) =>
      sum + (announcement.stats?.acknowledged || 0), 0
    );

    const responseRate = totalViews > 0 ? Math.round((totalAcknowledged / totalViews) * 100) : 0;

    return { active, scheduled, totalViews, responseRate };
  }, [announcements]);

  // Fetch initial data on component mount
  useEffect(() => {
    fetchInitialData();
  }, []);

  // Fetch announcements when filters change
  useEffect(() => {
    fetchAnnouncementsData();
  }, [currentPage, currentLimit, searchQuery, sortBy, sortOrder, selectedType, selectedStatus, selectedPriority]);

  const fetchInitialData = async () => {
    // Initial data fetching if needed
  };

  const fetchAnnouncementsData = async () => {
    await fetchAnnouncements({
      page: currentPage,
      limit: currentLimit,
      search: searchQuery,
      sortBy,
      sortOrder: sortOrder as "asc" | "desc",
      type: selectedType !== "all" ? selectedType : "",
      status: selectedStatus !== "all" ? selectedStatus : "",
      priority: selectedPriority !== "all" ? selectedPriority : "",
      isShowLoader: true
    });
  };

  const handleSearch = (value: string) => {
    setSearchQuery(value);
    setPage(1); // Reset to first page when searching
  };

  const handleSortChange = (value: string) => {
    setSortBy(value);
    setPage(1);
  };

  const handleFilterChange = (filterType: string, value: string) => {
    switch (filterType) {
      case "type":
        setSelectedType(value);
        break;
      case "status":
        setSelectedStatus(value);
        break;
      case "priority":
        setSelectedPriority(value);
        break;
    }
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  };

  const resetFormData = useCallback(() => {
    setFormData({
      title: "",
      content: "",
      type: { id: AnnouncementTypeEnum.General },
      priority: { id: AnnouncementPriorityEnum.Medium },
      visibility: { id: AnnouncementVisibilityEnum.All_Users },
      region: { id: "" },
      country: { id: "", name: "", code: "" },
      publishDate: "",
      expiryDate: "",
      isMobile: false,
      isEmail: false,
      isWeb: true,
      isText: true
    });
  }, []);

  // Reset form data when edit modal closes
  useEffect(() => {
    if (!showEditModal) {
      resetFormData();
    }
  }, [showEditModal, resetFormData]);

  const handleAddNew = useCallback(() => {
    setSuccessMessage(""); // Clear any existing success message
    resetFormData();
    setShowAddModal(true);
  }, [resetFormData]);

  const handleEdit = useCallback((announcement: Announcement) => {
    setSuccessMessage(""); // Clear any existing success message
    console.log("🔍 Editing announcement:", announcement);
    console.log("🔍 Original dates:", { 
      publishDate: announcement.publishDate, 
      expiryDate: announcement.expiryDate 
    });
    
    // Map the announcement data to form data
    const mappedData = {
      title: announcement.title || "",
      content: announcement.content || "",
      type: { id: Number(announcement.type?.id) || AnnouncementTypeEnum.General },
      priority: { id: Number(announcement.priority?.id) || AnnouncementPriorityEnum.Medium },
      visibility: { id: Number(announcement.visibility?.id) || AnnouncementVisibilityEnum.All_Users },
      region: announcement.region || { id: "" },
      country: announcement.country || { id: "", name: "", code: "" },
      publishDate: announcement.publishDate ? new Date(announcement.publishDate).toISOString().slice(0, 16) : "",
      expiryDate: announcement.expiryDate ? new Date(announcement.expiryDate).toISOString().slice(0, 16) : "",
      isMobile: announcement.isMobile || false,
      isEmail: announcement.isEmail || false,
      isWeb: announcement.isWeb || true,
      isText: announcement.isText !== false
    };
    
    console.log("🔍 Mapped form data:", mappedData);
    setFormData(mappedData);
    setShowEditModal(announcement);
  }, []);

  const handleDelete = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this announcement?")) {
      try {
        await deleteExistingAnnouncement(id);
        // Refresh the current page data
        await fetchAnnouncementsData();
      } catch (error) {
        console.error("Error deleting announcement:", error);
      }
    }
  };

  const handleSubmit = useCallback(async (data: any, isEdit: boolean = false) => {
    try {
      let result;
      if (isEdit && showEditModal) {
        console.log("🔍 Updating announcement with ID:", showEditModal.id);
        console.log("🔍 Update data:", data);
        console.log("🔍 Region data:", { region: data.region, country: data.country });
        result = await updateExistingAnnouncement(showEditModal.id, data);
        console.log("🔍 Update result:", result);
        
        // Check if the action was rejected
        if (result.type?.endsWith("/rejected")) {
          throw new Error(result.payload || "Failed to update announcement");
        }
        
        // Check if the action was fulfilled
        if (result.type?.endsWith("/fulfilled")) {
          setSuccessMessage("Announcement updated successfully!");
        } else {
          throw new Error("Update action did not complete successfully");
        }
      } else {
        console.log("🔍 Creating new announcement with data:", data);
        console.log("🔍 Region data:", { region: data.region, country: data.country });
        result = await createNewAnnouncement(data);
        if (result.type?.endsWith("/rejected")) {
          throw new Error(result.payload || "Failed to create announcement");
        }
        setSuccessMessage("Announcement created successfully!");
      }

      // Close modal and reset form data
      setShowAddModal(false);
      setShowEditModal(null);
      resetFormData();

      // Reset to first page to show the latest announcements
      setPage(1);

      // Fetch latest announcements to show in listing
      await fetchAnnouncementsData();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error saving announcement:", error);
      // Keep modal open on error so user can retry
      setSuccessMessage("");
    }
  }, [showEditModal, updateExistingAnnouncement, createNewAnnouncement, resetFormData, fetchAnnouncementsData, setPage]);

  const handleCloseModal = useCallback(() => {
    setSuccessMessage(""); // Clear any existing success message
    setShowAddModal(false);
    setShowEditModal(null);
    resetFormData();
  }, [resetFormData]);

  // Helper functions
  const getTypeName = (id: any) => {
    const numId = typeof id === "string" ? parseInt(id) : id;
    switch (numId) {
      case AnnouncementTypeEnum.General:
        return "General";
      case AnnouncementTypeEnum.Maintenance:
        return "Maintenance";
      case AnnouncementTypeEnum.Feature_Update:
        return "Feature Update";
      case AnnouncementTypeEnum.Alert:
        return "Alert";
      default:
        return "Unknown";
    }
  };

  const getPriorityName = (id: any) => {
    const numId = typeof id === "string" ? parseInt(id) : id;
    switch (numId) {
      case AnnouncementPriorityEnum.Low:
        return "Low";
      case AnnouncementPriorityEnum.Medium:
        return "Medium";
      case AnnouncementPriorityEnum.High:
        return "High";
      default:
        return "Unknown";
    }
  };

  const getVisibilityName = (id: any) => {
    const numId = typeof id === "string" ? parseInt(id) : id;
    switch (numId) {
      case AnnouncementVisibilityEnum.All_Users:
        return "All Users";
      case AnnouncementVisibilityEnum.Clients:
        return "Clients";
      case AnnouncementVisibilityEnum.Professionals:
        return "Professionals";
      default:
        return "Unknown";
    }
  };

  const getPriorityVariant = (id: any) => {
    const numId = typeof id === "string" ? parseInt(id) : id;
    switch (numId) {
      case AnnouncementPriorityEnum.High:
        return "destructive";
      case AnnouncementPriorityEnum.Medium:
        return "warning";
      default:
        return "info";
    }
  };

  const getTypeIcon = (id: any) => {
    const numId = typeof id === "string" ? parseInt(id) : id;
    switch (numId) {
      case AnnouncementTypeEnum.Maintenance:
        return <Settings className="h-4 w-4" />;
      case AnnouncementTypeEnum.Feature_Update:
        return <Radio className="h-4 w-4" />;
      case AnnouncementTypeEnum.Alert:
        return <AlertCircle className="h-4 w-4" />;
      default:
        return <Bell className="h-4 w-4" />;
    }
  };

  const getAnnouncementStatus = (announcement: Announcement) => {
    const now = new Date();
    const publishDate = new Date(announcement.publishDate);
    const expiryDate = announcement.expiryDate ? new Date(announcement.expiryDate) : null;

    if (publishDate > now) {
      return { status: "scheduled", variant: "info" };
    } else if (expiryDate && expiryDate < now) {
      return { status: "expired", variant: "secondary" };
    } else {
      return { status: "active", variant: "success" };
    }
  };

    // Announcement Card Component
  const AnnouncementCard = ({ announcement }: { announcement: Announcement }) => {
    const statusInfo = getAnnouncementStatus(announcement);
    
    return (
      <Card className="hover:shadow-md transition-shadow" style={{ textAlign: "left" }}>
        <div className="p-6 space-y-4">
          {/* Header */}
          <div className="flex justify-between items-start">
            <div>
              <div className="flex items-center gap-2">
                <Badge variant="outline">{getTypeName(announcement.type?.id)}</Badge>
                <Badge variant={getPriorityVariant(announcement.priority?.id)}>
                  {getPriorityName(announcement.priority?.id)} Priority
                </Badge>
                <Badge variant={statusInfo.variant}>
                  {statusInfo.status.charAt(0).toUpperCase() + statusInfo.status.slice(1)}
                </Badge>
              </div>
              <h3 className="font-medium mt-2">{announcement.title}</h3>
            </div>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleEdit(announcement)}
              >
                <Edit className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-red-600"
                onClick={() => handleDelete(announcement.id)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Content */}
          {/* {(() => {
            // Check if content is HTML (based on isText flag or if content starts with HTML tags)
            const content = announcement.content || "";
            const startsWithHtmlTag = /^\s*<[^>]+>/.test(content);
            
            // If isText is false OR content starts with HTML tags, render as HTML
            if (announcement.isText === false || startsWithHtmlTag) {
              return (
                <div 
                  dangerouslySetInnerHTML={{ __html: content }}
                  className="text-gray-600 line-clamp-3 announcement-html-content"
                  style={{
                    overflow: "hidden",
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical"
                  }}
                />
              );
            }
            
            // Otherwise render as plain text
            return <p className="text-gray-600 line-clamp-3">{content}</p>;
          })()} */}

          {/* Stats & Info */}
          <div className="flex items-center justify-between text-sm text-gray-500">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1">
                <Eye className="h-4 w-4" />
                <span>{announcement.stats?.views || 0}</span>
              </div>
              <div className="flex items-center gap-1">
                <CheckCircle className="h-4 w-4" />
                <span>{announcement.stats?.acknowledged || 0}</span>
              </div>
            </div>
            <span>
              {announcement.publishDate ? new Date(announcement.publishDate).toLocaleDateString() : "N/A"}
            </span>
          </div>

          {/* Location & Targeting Info */}
          <div className="pt-4 border-t">
            <div className="space-y-3">
              {/* Location Info */}
              <div className="flex flex-wrap gap-2">
                {announcement.country?.name && (
                  <Badge variant="outline" className="flex items-center gap-1">
                    <Globe className="h-3 w-3" />
                    {announcement.country.name}
                  </Badge>
                )}
                {announcement.region?.id && (
                  <Badge variant="outline" className="flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    Region
                  </Badge>
                )}
              </div>
              
              {/* Targeting Info */}
              <div className="flex flex-wrap gap-2">
                <Badge variant="secondary">
                  {getVisibilityName(announcement.visibility?.id)}
                </Badge>
                {announcement.isWeb && <Badge variant="outline">Web</Badge>}
                {announcement.isEmail && <Badge variant="outline">Email</Badge>}
                {announcement.isMobile && <Badge variant="outline">Mobile</Badge>}
              </div>
            </div>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" style={{ textAlign: "left" }}>
      <AdminHeader />
      <main className="flex-1 lg:pl-64 pt-16">
        <div className="p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Announcements</h1>
                <p className="text-gray-600 mt-1">Create and manage platform announcements</p>
              </div>
              <Button
                onClick={handleAddNew}
                className="flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                New Announcement
              </Button>
            </div>

            {/* Success Message */}
            {successMessage && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <p className="text-green-800 font-medium">{successMessage}</p>
                </div>
              </div>
            )}

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Active</p>
                    <p className="text-2xl font-semibold">{stats.active}</p>
                  </div>
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Radio className="h-5 w-5 text-green-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Scheduled</p>
                    <p className="text-2xl font-semibold">{stats.scheduled}</p>
                  </div>
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Clock className="h-5 w-5 text-blue-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Total Views</p>
                    <p className="text-2xl font-semibold">{stats.totalViews.toLocaleString()}</p>
                  </div>
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Eye className="h-5 w-5 text-purple-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Response Rate</p>
                    <p className="text-2xl font-semibold">{stats.responseRate}%</p>
                  </div>
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-yellow-600" />
                  </div>
                </div>
              </Card>
            </div>

            {/* Search and Filters */}
            <Card className="p-4">
              <div className="flex flex-col lg:flex-row justify-between gap-4">
                <div className="flex flex-col sm:flex-row items-center gap-4">
                  <div className="relative flex-1 w-full sm:min-w-[300px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search announcements..."
                      value={searchQuery}
                      onChange={(e) => handleSearch(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border rounded-lg"
                    />
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setShowFilters(!showFilters)}
                    className="flex items-center gap-2 w-full sm:w-auto"
                  >
                    <Filter className="h-5 w-5" />
                    Filters
                  </Button>
                </div>

                <div className="flex flex-col sm:flex-row items-center gap-4 w-full lg:w-auto">
                  <select
                    value={selectedType}
                    onChange={(e) => handleFilterChange("type", e.target.value)}
                    className="px-4 py-2 border rounded-lg w-full sm:w-auto"
                  >
                    <option value="all">All Types</option>
                    <option value="1">General</option>
                    <option value="2">Maintenance</option>
                    <option value="3">Feature Update</option>
                    <option value="4">Alert</option>
                  </select>

                  <select
                    value={selectedStatus}
                    onChange={(e) => handleFilterChange("status", e.target.value)}
                    className="px-4 py-2 border rounded-lg w-full sm:w-auto"
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="expired">Expired</option>
                  </select>

                  <select
                    value={selectedPriority}
                    onChange={(e) => handleFilterChange("priority", e.target.value)}
                    className="px-4 py-2 border rounded-lg w-full sm:w-auto"
                  >
                    <option value="all">All Priorities</option>
                    <option value="1">Low</option>
                    <option value="2">Medium</option>
                    <option value="3">High</option>
                  </select>

                  <select
                    value={`${sortBy}-${sortOrder}`}
                    onChange={(e) => {
                      const [field, order] = e.target.value.split("-");
                      setSortBy(field);
                      setSortOrder(order as "asc" | "desc");
                    }}
                    className="px-4 py-2 border rounded-lg w-full sm:w-auto"
                  >
                    <option value="createdAt-desc">Newest First</option>
                    <option value="createdAt-asc">Oldest First</option>
                    <option value="title-asc">Title A-Z</option>
                    <option value="title-desc">Title Z-A</option>
                    <option value="priority-desc">Priority (High-Low)</option>
                    <option value="priority-asc">Priority (Low-High)</option>
                  </select>
                </div>
              </div>
            </Card>

            {/* Announcements Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {loading ? (
                <div className="col-span-2 text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Loading announcements...</p>
                </div>
              ) : announcements && announcements.length > 0 ? (
                <>
                  {announcements
                    .filter(announcement => announcement && announcement.id)
                    .map((announcement) => (
                      <div key={announcement.id} className="col-span-1">
                        <AnnouncementCard announcement={announcement} />
                      </div>
                    ))}
                </>
              ) : (
                <div className="col-span-2 text-center py-8">
                  <Bell className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No announcements found</h3>
                  <p className="text-gray-600 mb-4">
                    {!loading && !error
                      ? "The API is working but there are no announcements in the database. Create your first announcement to get started."
                      : "Get started by creating your first announcement."
                    }
                  </p>
                  <Button onClick={handleAddNew}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Announcement
                  </Button>
                </div>
              )}
            </div>

            {/* Pagination */}
            {announcements && announcements.length > 0 && (
              <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-700">Show</span>
                  <select
                    value={currentLimit}
                    onChange={(e) => handleLimitChange(Number(e.target.value))}
                    className="px-2 py-1 border rounded text-sm"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                  <span className="text-sm text-gray-700">of {totalCount} results</span>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage <= 1}
                  >
                    Previous
                  </Button>

                  <span className="text-sm text-gray-700">
                    Page {currentPage} of {Math.ceil(totalCount / currentLimit)}
                  </span>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={!hasNextPage}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Modals */}
      {(showAddModal || showEditModal) && (
        <AnnouncementModal
          announcement={showEditModal}
          isOpen={true}
          onClose={handleCloseModal}
          onSubmit={handleSubmit}
          formData={formData}
          setFormData={setFormData}
        />
      )}

      {/* <SharedFooter3 /> */}
    </div>
  );
};
export default Announcements;